import { useEffect, useState } from "react";
import { Building2, Calculator, MapPin, Truck } from "lucide-react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { ActionBar, Button, Field, SelectField } from "@fawkes/ui";

import { formatEuro } from "../i18n/format";

// De tegel Vervoer op het dossier, zoals de mockup hem tekent: aanlevering (opgehaald of gebracht,
// afstand, spoed, retour rekenen), het ophaaladres (welk adres, dag en tijd) en herkomst en rit
// (praktijk, derde partij, ritnummer, wie ophaalde). Onderaan wat de rit kost volgens het tarief —
// de server rekent dat en zet het als regel op de factuur; hier staat alleen waar het bedrag
// vandaan komt.

export interface VervoerGegevens {
  herkomst: string | null;
  spoed: boolean | null;
  ophaalAdres: string | null;
  ophaalAdresSoort: string | null;
  ophaalDatum: string | null;
  ophaalTijd: string | null;
  afstandKm: number | null;
  retourRekenen: boolean | null;
  chauffeur: string | null;
  ritNummer: string | null;
  ontvangenDoor: string | null;
  dierenartsRelatieId: number | null;
  derdePartijRelatieId: number | null;
  ritKosten: {
    tariefNaam: string | null;
    km: number;
    starttarief: number;
    extraKm: number;
    bedrag: number;
    btwPercentage: number | null;
    retour: boolean;
    spoed: boolean;
  } | null;
  eigenaar: { adres: string | null; postcode: string | null; plaats: string | null } | null;
}

interface Afspraak {
  relatieId: number;
  relatieNaam: string | null;
}

type Aanlevering = "OPHALEN" | "GEBRACHT" | "";

interface Invoer {
  aanlevering: Aanlevering;
  afstandKm: string;
  spoed: boolean;
  retourRekenen: boolean;
  adresSoort: string;
  ophaalAdres: string;
  ophaalDatum: string;
  ophaalTijd: string;
  dierenartsRelatieId: string;
  derdePartijRelatieId: string;
  ritNummer: string;
  chauffeur: string;
  ontvangenDoor: string;
}

const vanGegevens = (d: VervoerGegevens): Invoer => ({
  aanlevering: d.herkomst === "OPHALEN_BIJ_EIGENAAR" ? "OPHALEN" : d.herkomst === "DOOR_EIGENAAR_GEBRACHT" ? "GEBRACHT" : "",
  afstandKm: d.afstandKm != null ? String(d.afstandKm) : "",
  spoed: d.spoed === true,
  // Leeg = wat het tarief zegt; de mockup laat het vinkje standaard aan.
  retourRekenen: d.retourRekenen !== false,
  adresSoort: d.ophaalAdresSoort ?? "EIGENAAR",
  ophaalAdres: d.ophaalAdres ?? "",
  ophaalDatum: d.ophaalDatum ?? "",
  ophaalTijd: (d.ophaalTijd ?? "").slice(0, 5),
  dierenartsRelatieId: d.dierenartsRelatieId != null ? String(d.dierenartsRelatieId) : "",
  derdePartijRelatieId: d.derdePartijRelatieId != null ? String(d.derdePartijRelatieId) : "",
  ritNummer: d.ritNummer ?? "",
  chauffeur: d.chauffeur ?? "",
  ontvangenDoor: d.ontvangenDoor ?? "",
});

export function CrematieDossierVervoerTegel({
  id,
  gegevens,
  onOpgeslagen,
}: {
  id: string;
  gegevens: VervoerGegevens;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [invoer, setInvoer] = useState<Invoer>(() => vanGegevens(gegevens));
  const [notice, setNotice] = useState<"conflict" | "forbidden" | "error" | null>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  const sleutel = JSON.stringify(gegevens);
  useEffect(() => {
    setInvoer(vanGegevens(gegevens));
    setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel]);

  // De praktijken en derde partijen komen uit de prijsafspraken: dat zijn de relaties waar het
  // crematorium mee werkt. Fouten (geen recht, geen module) laten de lijst gewoon leeg.
  const praktijken = useFeed<Afspraak>("crematorium/vergoedingen?rol=DIERENARTS&alleenActief=false");
  const derden = useFeed<Afspraak>("crematorium/vergoedingen?rol=DERDE_PARTIJ&alleenActief=false");

  const zet = <K extends keyof Invoer>(veld: K, waarde: Invoer[K]) =>
    setInvoer((huidig) => ({ ...huidig, [veld]: waarde }));

  const eigenaarAdres = [gegevens.eigenaar?.adres, [gegevens.eigenaar?.postcode, gegevens.eigenaar?.plaats].filter(Boolean).join(" ")]
    .filter((deel) => deel && deel.trim() !== "").join(", ");
  const praktijkNaam = praktijken.find((p) => String(p.relatieId) === invoer.dierenartsRelatieId)?.relatieNaam ?? null;

  /** De herkomst volgt uit de aanlevering, en anders uit wie het dier bracht. */
  const herkomst = invoer.aanlevering === "OPHALEN" ? "OPHALEN_BIJ_EIGENAAR"
    : invoer.aanlevering === "GEBRACHT" ? "DOOR_EIGENAAR_GEBRACHT"
    : invoer.dierenartsRelatieId ? "VIA_DIERENARTS"
    : invoer.derdePartijRelatieId ? "VIA_DERDE_PARTIJ"
    : null;
  /** Het ophaaladres als tekst op het dossier: het adres dat de keuze aanwijst. */
  const ophaalAdres = invoer.adresSoort === "EIGENAAR" ? eigenaarAdres
    : invoer.adresSoort === "PRAKTIJK" ? (praktijkNaam ?? "")
    : invoer.ophaalAdres;

  const getal = (s: string): number | null => {
    const n = Number(s.replace(",", "."));
    return s.trim() === "" || !Number.isFinite(n) ? null : n;
  };
  const leeg = (s: string): string | null => (s.trim() === "" ? null : s.trim());

  const opslaan = () => {
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${id}/vervoer`,
        method: "put",
        values: {
          herkomst,
          ophaalAdres: leeg(ophaalAdres),
          ophaalAdresSoort: invoer.adresSoort || null,
          ophaalDatum: leeg(invoer.ophaalDatum),
          ophaalTijd: leeg(invoer.ophaalTijd),
          afstandKm: getal(invoer.afstandKm),
          spoed: invoer.spoed,
          retourRekenen: invoer.retourRekenen,
          chauffeur: leeg(invoer.chauffeur),
          ritNummer: leeg(invoer.ritNummer),
          ontvangenDoor: leeg(invoer.ontvangenDoor),
          dierenartsRelatieId: getal(invoer.dierenartsRelatieId),
          derdePartijRelatieId: getal(invoer.derdePartijRelatieId),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onOpgeslagen(),
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice(code === 409 ? "conflict" : code === 403 ? "forbidden" : "error");
        },
      },
    );
  };

  const rit = gegevens.ritKosten;
  const euro = (n: number) => formatEuro(n, locale);

  return (
    <section className="fui-tile crem-tile crem-tile--vervoer">
      <header className="fui-tile__header">
        <h4 className="fui-tile__title">
          <span className="fawkes-relatie-tiletitel"><Truck size={18} aria-hidden="true" />{t("crematieDetail.section.vervoer")}</span>
        </h4>
      </header>

      <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`crematoriumTarief.notice.${notice}`)}
          </div>
        )}

        {/* Aanlevering: opgehaald of gebracht, en wat de rit bepaalt. */}
        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><Truck size={18} aria-hidden="true" />{t("crematieDetail.groep.aanlevering")}</h5>
          <div className="crem-checkrij">
            <label className="crem-check">
              <input type="checkbox" checked={invoer.aanlevering === "OPHALEN"}
                onChange={(e) => zet("aanlevering", e.target.checked ? "OPHALEN" : "")} />
              {" "}{t("crematieDetail.vervoer.opgehaald")}
            </label>
            <label className="crem-check">
              <input type="checkbox" checked={invoer.aanlevering === "GEBRACHT"}
                onChange={(e) => zet("aanlevering", e.target.checked ? "GEBRACHT" : "")} />
              {" "}{t("crematieDetail.vervoer.gebracht")}
            </label>
          </div>
          <div className="fawkes-relatie-grid">
            <Field label={t("crematieDetail.vervoer.afstandEnkel")} inputMode="decimal" value={invoer.afstandKm}
              onChange={(e) => zet("afstandKm", e.target.value)} />
            <SelectField label={t("crematieDetail.vervoer.ophaalmoment")} value={invoer.spoed ? "ja" : "nee"}
              onChange={(e) => zet("spoed", e.target.value === "ja")}
              options={[
                { value: "nee", label: t("crematieDetail.vervoer.regulier") },
                { value: "ja", label: t("crematieDetail.vervoer.spoed") },
              ]} />
          </div>
          <label className="crem-check">
            <input type="checkbox" checked={invoer.retourRekenen} onChange={(e) => zet("retourRekenen", e.target.checked)} />
            {" "}{t("crematieDetail.vervoer.retourRekenen")}
          </label>
        </section>

        {/* Ophaaladres: welk adres, en wanneer. */}
        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><MapPin size={18} aria-hidden="true" />{t("crematieDetail.groep.ophaaladres")}</h5>
          <div className="fawkes-relatie-grid crem-grid--1">
            <SelectField label={t("crematieDetail.vervoer.adres")} value={invoer.adresSoort}
              onChange={(e) => zet("adresSoort", e.target.value)}
              options={[
                { value: "EIGENAAR", label: t("crematieDetail.vervoer.adresEigenaar", { args: [eigenaarAdres || "—"] }) },
                { value: "PRAKTIJK", label: t("crematieDetail.vervoer.adresPraktijk", { args: [praktijkNaam ?? "—"] }) },
                { value: "ANDERS", label: t("crematieDetail.vervoer.adresAnders") },
              ]} />
            {invoer.adresSoort === "ANDERS" && (
              <Field label={t("crematieDetail.crematie.ophaalAdres")} value={invoer.ophaalAdres}
                onChange={(e) => zet("ophaalAdres", e.target.value)} />
            )}
          </div>
          <div className="fawkes-relatie-grid">
            <Field label={t("crematieDetail.vervoer.ophaalDatum")} type="date" value={invoer.ophaalDatum}
              onChange={(e) => zet("ophaalDatum", e.target.value)} />
            <Field label={t("crematieDetail.vervoer.tijd")} type="time" value={invoer.ophaalTijd}
              onChange={(e) => zet("ophaalTijd", e.target.value)} />
          </div>
        </section>

        {/* Herkomst en rit: via wie het dier kwam, en de rit zelf. */}
        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><Building2 size={18} aria-hidden="true" />{t("crematieDetail.groep.rit")}</h5>
          <div className="fawkes-relatie-grid">
            <SelectField label={t("crematieDetail.vervoer.praktijk")} value={invoer.dierenartsRelatieId}
              onChange={(e) => zet("dierenartsRelatieId", e.target.value)}
              options={[{ value: "", label: "—" }, ...praktijken.map((p) => ({ value: String(p.relatieId), label: p.relatieNaam ?? String(p.relatieId) }))]} />
            <SelectField label={t("crematieDetail.vervoer.derdePartij")} value={invoer.derdePartijRelatieId}
              onChange={(e) => zet("derdePartijRelatieId", e.target.value)}
              options={[{ value: "", label: "—" }, ...derden.map((p) => ({ value: String(p.relatieId), label: p.relatieNaam ?? String(p.relatieId) }))]} />
          </div>
          <div className="fawkes-relatie-grid">
            <Field label={t("crematieDetail.crematie.rit")} value={invoer.ritNummer} onChange={(e) => zet("ritNummer", e.target.value)} />
            <Field label={t("crematieDetail.vervoer.opgehaaldDoor")} value={invoer.chauffeur} onChange={(e) => zet("chauffeur", e.target.value)} />
          </div>
          <div className="fawkes-relatie-grid crem-grid--1">
            <Field label={t("crematieDetail.crematie.ontvangen")} value={invoer.ontvangenDoor} onChange={(e) => zet("ontvangenDoor", e.target.value)} />
          </div>
        </section>

        {/* Waar het bedrag vandaan komt — de regel zelf staat bij de factuurregels. */}
        {rit && (
          <div className="crem-note" data-testid="ritkosten">
            <Calculator size={16} aria-hidden="true" />
            <span>
              {t("crematieDetail.vervoer.ritUitleg", {
                args: [String(rit.km), rit.retour ? t("crematieDetail.vervoer.retour") : t("crematieDetail.vervoer.enkel"), euro(rit.starttarief), String(rit.extraKm)],
              })}{" "}
              <strong>{euro(rit.bedrag)}</strong>
              {rit.spoed ? ` (${t("crematieDetail.vervoer.spoed").toLowerCase()})` : ""}
              {" — "}{t("crematieDetail.vervoer.ritOpFactuur")}
            </span>
          </div>
        )}
      </div>

      <ActionBar>
        <Button variant="secondary" disabled={bezig} onClick={() => setInvoer(vanGegevens(gegevens))}>
          {t("common.cancel")}
        </Button>
        <Button disabled={bezig} onClick={opslaan}>{t("common.save")}</Button>
      </ActionBar>
    </section>
  );
}

function useFeed<T>(url: string): T[] {
  const { data } = useCustom({ url, method: "get", errorNotification: false });
  const rijen = data?.data as T[] | { content?: T[] } | undefined;
  if (Array.isArray(rijen)) return rijen;
  return (rijen as { content?: T[] } | undefined)?.content ?? [];
}
